import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin( ScrollTrigger );

const NEIGHBOR_SHIFT_DURATION = 0.35;
const LABEL_SHIFT_DURATION = 0.25;

function shiftNeighbors( items, hoveredIndex, leftOffset, rightOffset ) {
	items.forEach( ( item, i ) => {
		if ( i === hoveredIndex ) {
			return;
		}

		const x = i < hoveredIndex ? -leftOffset : rightOffset;

		gsap.to( item, {
			x,
			duration: NEIGHBOR_SHIFT_DURATION,
			ease: 'power2.out',
		} );
	} );
}

function wireHover( items ) {
	items.forEach( ( item, index ) => {
		const frame = item.querySelector( '[data-mosaic="frame"]' );
		const cardEl = item.querySelector( '[data-mosaic="card"]' );

		if ( ! frame || ! cardEl ) {
			return;
		}

		const nameEl = item.querySelector( '[data-mosaic="name"]' );
		const labelEl = item.querySelector( '[data-mosaic="label"]' );

		function reveal() {
			// The card overflows past the image's own box (see index.php's
			// `-inset-*`/`start-*`/`end-*` classes on [data-mosaic="card"]),
			// so it needs to paint above whichever neighbor items happen to
			// follow it in DOM order.
			item.style.zIndex = '10';
			gsap.to( cardEl, { opacity: 1, duration: 0.25 } );
			cardEl.style.pointerEvents = 'auto';

			if ( nameEl ) {
				gsap.to( nameEl, { opacity: 1, duration: 0.2 } );
			}

			// The card is laid out (if not yet visible) as soon as the
			// component mounts, so its box can be measured against the
			// frame's before the opacity tween even starts. Neighbors only
			// need to make room for however far the card actually overflows
			// past the frame on their side — measured live (and separately
			// per side, since the first/last item's card in index.php only
			// grows toward the row's interior) so it always tracks the
			// `-inset-*`/`start-*`/`end-*` classes on [data-mosaic="card"]
			// rather than a guessed constant.
			const frameRect = frame.getBoundingClientRect();
			const cardRect = cardEl.getBoundingClientRect();
			const leftOverflow = Math.max( 0, frameRect.left - cardRect.left );
			const rightOverflow = Math.max(
				0,
				cardRect.right - frameRect.right
			);

			shiftNeighbors( items, index, leftOverflow, rightOverflow );

			if ( labelEl ) {
				// The card overflows above the frame by that same amount;
				// if that overlaps the label's own box, lift the label
				// clear by exactly the overlap so it stays readable.
				const labelRect = labelEl.getBoundingClientRect();
				const overlap = labelRect.bottom - cardRect.top;

				gsap.to( labelEl, {
					y: overlap > 0 ? -overlap : 0,
					duration: LABEL_SHIFT_DURATION,
					ease: 'power2.out',
				} );
			}
		}

		function unreveal() {
			gsap.to( cardEl, {
				opacity: 0,
				duration: 0.2,
				onComplete: () => {
					item.style.zIndex = '';
				},
			} );
			cardEl.style.pointerEvents = 'none';

			if ( nameEl ) {
				gsap.to( nameEl, { opacity: 0, duration: 0.2 } );
			}

			if ( labelEl ) {
				gsap.to( labelEl, {
					y: 0,
					duration: LABEL_SHIFT_DURATION,
					ease: 'power2.out',
				} );
			}

			shiftNeighbors( items, index, 0, 0 );
		}

		frame.addEventListener( 'mouseenter', reveal );
		frame.addEventListener( 'mouseleave', unreveal );
		frame.addEventListener( 'focusin', reveal );
		frame.addEventListener( 'focusout', unreveal );
	} );
}

// The card overlay is `position: absolute` inside `[data-mosaic="frame"]`
// and neighbor "make room" shifts are transforms on the neighbors
// themselves — neither ever changes an item's own layout box, so hovering
// never affects `rowEl.scrollWidth` (and therefore never needs to disturb
// the pinned scroll distance below).
function initPinnedHorizontalScroll( sectionEl, wrapperEl, rowEl ) {
	const distance = rowEl.scrollWidth - wrapperEl.clientWidth;

	if ( distance <= 0 ) {
		return;
	}

	gsap.to( rowEl, {
		x: -distance,
		ease: 'none',
		scrollTrigger: {
			trigger: sectionEl,
			start: 'top top',
			end: () => '+=' + ( rowEl.scrollWidth - wrapperEl.clientWidth ),
			scrub: 0.5,
			pin: true,
			invalidateOnRefresh: true,
		},
	} );

	window.addEventListener( 'resize', () => ScrollTrigger.refresh() );

	// #caseStudyMap (directly above this section) fetches its GeoJSON and
	// renders with D3 asynchronously; a placeholder aspect-ratio on its
	// `<svg>` (see SKILL.md's Case Study Map section) now reserves close
	// to its final height up front, but "close to" isn't exact, so still
	// refresh if its box changes once the real render lands. See SKILL.md's
	// Text Color Reveal section ("The refreshPriority trap") for why a
	// global refresh from here is safe even while #introStatement's own
	// ScrollTrigger might be mid-rebuild.
	const mapContainer = document.getElementById( 'caseStudyMap' );

	if ( mapContainer ) {
		new ResizeObserver( () => ScrollTrigger.refresh() ).observe(
			mapContainer
		);
	}
}

export function initCaseStudyMosaicScroll( sectionEl ) {
	if ( ! sectionEl ) {
		return;
	}

	const wrapperEl = sectionEl.querySelector(
		'[data-animate="mosaic-scroll"]'
	);
	const rowEl = sectionEl.querySelector( '[data-mosaic="row"]' );

	if ( ! wrapperEl || ! rowEl ) {
		return;
	}

	const items = Array.from(
		rowEl.querySelectorAll( '[data-mosaic="item"]' )
	);

	if ( ! items.length ) {
		return;
	}

	wireHover( items );

	// Relies on text-color-reveal.js's upfront `document.fonts.forEach(f =>
	// f.load())` call (both modules always init on the same page load) to
	// avoid the font-loadingdone ScrollTrigger-rebuild trap documented in
	// SKILL.md — if that module is ever removed, replicate the force-load
	// call here before this wait.
	document.fonts.ready.then( () => {
		initPinnedHorizontalScroll( sectionEl, wrapperEl, rowEl );
	} );
}
